import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { settingsService } from '../../services/settingsService';
import { driverGroupsService } from '../../services/driverGroupsService';
import { driversService } from '../../services/driversService';
import { DriverGroupVisibilityGroup } from '../../types/settings';
import { OrderAssignedDriverResponse } from '../../types/orders';
import { AdminDriversResponse } from '../../types/drivers';
import './Page.css';

const DriverGroupVisibility: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [groups, setGroups] = useState<DriverGroupVisibilityGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [selectedGroup, setSelectedGroup] = useState<DriverGroupVisibilityGroup | null>(null);
  const [groupDrivers, setGroupDrivers] = useState<OrderAssignedDriverResponse[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driversError, setDriversError] = useState('');
  const [driverIdInput, setDriverIdInput] = useState('');
  const [driverActionLoading, setDriverActionLoading] = useState(false);
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [driverSearchResults, setDriverSearchResults] = useState<AdminDriversResponse[]>([]);
  const [driverSearchLoading, setDriverSearchLoading] = useState(false);
  const [driverSearchError, setDriverSearchError] = useState('');

  const fetchVisibilityGroups = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await settingsService.getDriverGroupVisibility();
      if (response.success && response.data) {
        setGroups(response.data.driverGroups);
      } else {
        setError(response.error || 'Failed to fetch driver group visibility');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDrivers = async (groupId: number) => {
    setDriversLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.listDriversInGroup(groupId);
      if (response.success && response.data) {
        setGroupDrivers(response.data);
      } else {
        setDriversError(response.error || 'Failed to fetch group drivers');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriversLoading(false);
    }
  };

  useEffect(() => {
    fetchVisibilityGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupDrivers(selectedGroup.id);
    } else {
      setGroupDrivers([]);
    }
    setDriverSearchQuery('');
    setDriverSearchResults([]);
    setDriverIdInput('');
  }, [selectedGroup]);

  useEffect(() => {
    if (!selectedGroup) return;

    const trimmedQuery = driverSearchQuery.trim();
    if (trimmedQuery.length < 2) {
      setDriverSearchResults([]);
      setDriverSearchError('');
      setDriverSearchLoading(false);
      return;
    }

    setDriverSearchLoading(true);
    setDriverSearchError('');

    const timeoutId = window.setTimeout(async () => {
      try {
        const response = await driversService.getDrivers({
          searchQuery: trimmedQuery,
          page: 0,
          pageSize: 10,
          applicationStatus: null,
          driverState: null
        });

        if (response.success && response.data) {
          setDriverSearchResults(response.data);
        } else {
          setDriverSearchResults([]);
          setDriverSearchError(response.error || 'Failed to search drivers');
        }
      } catch (error) {
        setDriverSearchResults([]);
        setDriverSearchError('Network error. Please try again.');
      } finally {
        setDriverSearchLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [driverSearchQuery, selectedGroup]);

  const toggleVisibility = (groupId: number) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? { ...group, ordersVisible: !group.ordersVisible }
          : group
      )
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      const visibleIds = groups
        .filter((group) => group.ordersVisible)
        .map((group) => group.id);

      const response = await settingsService.updateDriverGroupVisibility({
        visibleDriverGroupIds: visibleIds
      });

      if (response.success) {
        setSuccessMessage(t('driverGroupVisibility.updated'));
        await fetchVisibilityGroups();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update driver group visibility');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDriver = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedGroup) return;

    const driverId = parseInt(driverIdInput, 10);
    if (Number.isNaN(driverId)) {
      setDriversError(t('driverGroups.invalidDriverId'));
      return;
    }

    setDriverActionLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.addDriverToGroup(selectedGroup.id, driverId);
      if (response.success) {
        setSuccessMessage(t('driverGroups.driverAdded'));
        setTimeout(() => setSuccessMessage(''), 3000);
        setDriverIdInput('');
        setDriverSearchQuery('');
        setDriverSearchResults([]);
        await fetchGroupDrivers(selectedGroup.id);
      } else {
        setDriversError(response.error || 'Failed to add driver');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleRemoveDriver = async (driverId: number) => {
    if (!selectedGroup) return;

    setDriverActionLoading(true);
    setDriversError('');

    try {
      const response = await driverGroupsService.removeDriverFromGroup(selectedGroup.id, driverId);
      if (response.success) {
        setSuccessMessage(t('driverGroups.driverRemoved'));
        setTimeout(() => setSuccessMessage(''), 3000);
        await fetchGroupDrivers(selectedGroup.id);
      } else {
        setDriversError(response.error || 'Failed to remove driver');
      }
    } catch (error) {
      setDriversError('Network error. Please try again.');
    } finally {
      setDriverActionLoading(false);
    }
  };

  const handleSelectDriver = (driver: AdminDriversResponse) => {
    setDriverIdInput(driver.driver.id.toString());
    setDriverSearchQuery(driver.driver.fullName);
    setDriverSearchResults([]);
  };

  return (
    <div className="page">
      <div className="page-header with-actions">
        <div>
          <h1>{t('driverGroupVisibility.title')}</h1>
          <p>{t('driverGroupVisibility.subtitle')}</p>
        </div>
        <div className="page-header-actions">
          <button
            type="button"
            className="header-action-button secondary"
            onClick={() => navigate('/settings')}
          >
            {t('common.back')}
          </button>
          <button
            type="button"
            className="header-action-button"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? t('driverGroupVisibility.saving') : t('driverGroupVisibility.save')}
          </button>
        </div>
      </div>

      <div className="page-content">
        {error && <div className="error-message">{error}</div>}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px'
            }}
          >
            {successMessage}
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-container">{t('driverGroupVisibility.loading')}</div>
          ) : groups.length === 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('common.name')}</th>
                  <th>{t('driverGroupVisibility.ordersVisible')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={4} className="no-data">
                    {t('driverGroupVisibility.noGroups')}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t('common.name')}</th>
                  <th>{t('driverGroupVisibility.ordersVisible')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>{group.id}</td>
                    <td>{group.name}</td>
                    <td>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={group.ordersVisible}
                          onChange={() => toggleVisibility(group.id)}
                          disabled={saving}
                        />
                        {group.ordersVisible ? t('common.active') : t('common.inactive')}
                      </label>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="table-action-button"
                          onClick={() => setSelectedGroup(group)}
                        >
                          {t('driverGroups.manageDrivers')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="table-container" style={{ padding: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
            <h3 style={{ margin: 0 }}>{t('driverGroups.driversTitle')}</h3>
            {selectedGroup && (
              <span style={{ color: '#6c757d' }}>
                {t('driverGroups.groupLabel')}: {selectedGroup.name}
              </span>
            )}
          </div>

          {!selectedGroup ? (
            <div className="no-data" style={{ padding: '2rem' }}>
              {t('driverGroups.selectGroupHint')}
            </div>
          ) : (
            <>
              <form
                onSubmit={handleAddDriver}
                style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap' }}
              >
                <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '360px' }}>
                  <input
                    type="text"
                    className="search-input"
                    placeholder={t('driverGroups.searchDriverPlaceholder')}
                    value={driverSearchQuery}
                    onChange={(e) => setDriverSearchQuery(e.target.value)}
                  />
                  {driverSearchLoading && (
                    <div className="search-suggestions loading">
                      {t('driverGroups.searchingDrivers')}
                    </div>
                  )}
                  {!driverSearchLoading && driverSearchResults.length > 0 && (
                    <div className="search-suggestions">
                      {driverSearchResults.map((driver) => (
                        <button
                          key={driver.driver.id}
                          type="button"
                          className="search-suggestion"
                          onClick={() => handleSelectDriver(driver)}
                        >
                          {driver.driver.id} - {driver.driver.fullName}
                        </button>
                      ))}
                    </div>
                  )}
                  {!driverSearchLoading && driverSearchError && (
                    <div className="search-suggestions error">
                      {driverSearchError}
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  className="search-input"
                  placeholder={t('driverGroups.addDriverPlaceholder')}
                  value={driverIdInput}
                  onChange={(e) => setDriverIdInput(e.target.value)}
                  style={{ maxWidth: '200px' }}
                />
                <button
                  type="submit"
                  className="search-button"
                  disabled={driverActionLoading}
                >
                  {t('driverGroups.addDriver')}
                </button>
              </form>

              {driversError && (
                <div className="error-message" style={{ marginTop: '1rem' }}>
                  {driversError}
                </div>
              )}

              {driversLoading ? (
                <div className="loading-container">{t('driverGroups.loadingDrivers')}</div>
              ) : groupDrivers.length === 0 ? (
                <div className="no-data" style={{ padding: '2rem' }}>
                  {t('driverGroups.noDriversInGroup')}
                </div>
              ) : (
                <table className="data-table" style={{ marginTop: '1rem' }}>
                  <thead>
                    <tr>
                      <th>{t('driverGroups.driverId')}</th>
                      <th>{t('drivers.fullName')}</th>
                      <th>{t('common.phone')}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupDrivers.map((driver) => (
                      <tr key={driver.id}>
                        <td>{driver.id}</td>
                        <td>{driver.fullName}</td>
                        <td>{driver.phone}</td>
                        <td>
                          <button
                            type="button"
                            className="table-action-button danger"
                            onClick={() => handleRemoveDriver(driver.id)}
                            disabled={driverActionLoading}
                          >
                            {t('driverGroups.removeDriver')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverGroupVisibility;
